/**
 * BACKOFFICE LOGISTIQUE — Partenaires & rosters (#662).
 * Méthodes extraites telles quelles du god-service `backoffice.service.ts`
 * (workstream C) : partenaires de livraison (deliveryPartners), équipe RH
 * (employees / employees_public + CFs invite/activate/resend), partenaires
 * d'expédition (shippingPartners) et team logistique (logisticsTeam).
 *
 * NB #662 : l'ancienne méthode privée `updatePartnerStats` (transaction
 * #413 sur deliveryPartners.stats via computeNextPartnerStats) était MORTE
 * — plus aucun appelant depuis que les stats partenaire sont incrémentées
 * serveur par la CF transitionShipmentStatus (#399 PR C). Supprimée lors de
 * l'extraction ; la fonction pure `computeNextPartnerStats`
 * (partner-stats.util.ts) reste la référence du calcul côté serveur.
 */

import { Injectable } from '@angular/core';
import { Firestore, collection, getDocs, doc, updateDoc, getDoc, addDoc, deleteDoc } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import {
  DeliveryPartner,
  TeamMember,
  ShippingPartner,
  LogisticsTeamMember,
  ShippingMethodSubType,
  LogisticsRole
} from '../models/backoffice.models';

@Injectable({ providedIn: 'root' })
export class PartnerService {

  constructor(
    private firestore: Firestore,
    private functions: Functions
  ) {}

  // ── Partenaires CRUD ──

  async getAllPartners(): Promise<DeliveryPartner[]> {
    const ref = collection(this.firestore, 'deliveryPartners');
    const snap = await getDocs(ref);
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as DeliveryPartner))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  async getPartnerById(partnerId: string): Promise<DeliveryPartner | null> {
    const ref = doc(this.firestore, 'deliveryPartners', partnerId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() } as DeliveryPartner;
  }

  async createPartner(data: Partial<DeliveryPartner>): Promise<string> {
    const ref = collection(this.firestore, 'deliveryPartners');
    const now = new Date();
    const docRef = await addDoc(ref, {
      ...data,
      stats: { totalDeliveries: 0, successRate: 100 },
      createdAt: now,
      updatedAt: now,
    });
    return docRef.id;
  }

  async updatePartner(partnerId: string, data: Partial<DeliveryPartner>): Promise<void> {
    const ref = doc(this.firestore, 'deliveryPartners', partnerId);
    await updateDoc(ref, { ...data, updatedAt: new Date() });
  }

  async deletePartner(partnerId: string): Promise<void> {
    const ref = doc(this.firestore, 'deliveryPartners', partnerId);
    await deleteDoc(ref);
  }

  // ── Équipe CRUD ──

  async getAllTeamMembers(): Promise<TeamMember[]> {
    const ref = collection(this.firestore, 'employees');
    const snap = await getDocs(ref);
    return snap.docs.map(d => {
      const raw = d.data();
      // Normalize: befret-backoffice uses displayName/phoneNumber, invite flow uses name/phone
      return {
        id: d.id,
        name: raw['name'] || raw['displayName'] || raw['email'] || 'Sans nom',
        role: raw['role'] || raw['ability'] || 'warehouse',
        location: raw['location'] || '',
        phone: raw['phone'] || raw['phoneNumber'] || '',
        email: raw['email'] || '',
        photoUrl: raw['photoUrl'] || raw['photoURL'] || '',
        status: raw['status'] || 'active',
        contractType: raw['contractType'],
        department: raw['department'] || '',
        invitationStatus: raw['invitationStatus'] || 'none',
        invitationToken: raw['invitationToken'],
        invitationExpiresAt: raw['invitationExpiresAt'],
        invitedBy: raw['invitedBy'] || raw['createdBy'] || '',
        userUid: raw['userUid'],
        activatedAt: raw['activatedAt'],
        createdAt: raw['createdAt'],
        updatedAt: raw['updatedAt'],
        source: raw['invitationStatus'] ? 'invite' : 'backoffice',
      } as TeamMember;
    }).sort((a, b) => a.name.localeCompare(b.name));
  }

  /**
   * Variante "light" basée sur le mirror non sensible employees_public
   * (T20 / W4 PR2c). Retourne le sous-ensemble des champs publics
   * nécessaires aux sélecteurs cross-team (auto-complete agent dans
   * ticket-detail, candidate-detail, crm/accounts, layout impersonate
   * search). Les champs sensibles (location, phone, contractType,
   * invitation*, audit metadata) sont laissés vides / undefined :
   * un caller qui a besoin de ces champs doit utiliser
   * `getAllTeamMembers()` (full) ET être derrière un guard manager+.
   */
  async getAllTeamMembersLight(): Promise<TeamMember[]> {
    const ref = collection(this.firestore, 'employees_public');
    const snap = await getDocs(ref);
    return snap.docs.map(d => {
      const raw = d.data();
      const fullName = `${raw['firstName'] || ''} ${raw['lastName'] || ''}`.trim();
      return {
        id: d.id,
        name: raw['name'] || fullName || raw['email'] || 'Sans nom',
        role: raw['role'] || 'warehouse',
        location: '',
        phone: '',
        email: raw['email'] || '',
        photoUrl: raw['photoURL'] || '',
        status: raw['status'] || 'active',
        contractType: undefined,
        department: raw['department'] || '',
        invitationStatus: 'none',
        invitationToken: undefined,
        invitationExpiresAt: undefined,
        invitedBy: '',
        userUid: raw['authUid'],
        activatedAt: undefined,
        createdAt: raw['createdAt'],
        updatedAt: undefined,
        source: 'backoffice',
      } as TeamMember;
    }).sort((a, b) => a.name.localeCompare(b.name));
  }

  async createTeamMember(data: Partial<TeamMember>): Promise<string> {
    const ref = collection(this.firestore, 'employees');
    const now = new Date();
    const docRef = await addDoc(ref, { ...data, createdAt: now, updatedAt: now });
    return docRef.id;
  }

  async updateTeamMember(memberId: string, data: Partial<TeamMember>): Promise<void> {
    const ref = doc(this.firestore, 'employees', memberId);
    await updateDoc(ref, { ...data, updatedAt: new Date() });
  }

  /**
   * #2073 — suppression par la fonction tracée `deleteEmployee` (compte Auth, secrets
   * du second facteur et fiche ; motif consigné dans `rbac_audit`), plus par un
   * `deleteDoc` navigateur.
   */
  async deleteTeamMember(memberId: string, reason: string): Promise<void> {
    const fn = httpsCallable<{ employeeId: string; reason: string }, { success: boolean }>(this.functions, 'deleteEmployee');
    await fn({ employeeId: memberId, reason });
  }

  /**
   * #2073 — désactivation / réactivation par la fonction tracée `setEmployeeStatus` :
   * compte Auth désactivé, jetons révoqués et droits retirés (ou rétablis), motif
   * consigné. Le `status` ne s'écrit plus depuis le navigateur.
   */
  async setEmployeeStatus(memberId: string, status: 'active' | 'inactive', reason: string): Promise<void> {
    const fn = httpsCallable<{ employeeId: string; status: string; reason: string }, { success: boolean }>(this.functions, 'setEmployeeStatus');
    await fn({ employeeId: memberId, status, reason });
  }

  // ── HR — Cloud Functions ──

  async inviteEmployee(data: {
    name: string;
    email: string;
    role: string;
    contractType?: string;
    location?: string;
    department?: string;
    phone?: string;
    groupIds?: string[];
    hireDate?: string;
    contractStartDate?: string;
    contractEndDate?: string;
    managerId?: string;
  }): Promise<{ teamMemberId: string; emailSent: boolean; failedGroups: string[] }> {
    const fn = httpsCallable<any, { success: boolean; teamMemberId: string; emailSent: boolean; failedGroups: string[] }>(this.functions, 'inviteEmployee');
    const result = await fn(data);
    return {
      teamMemberId: result.data.teamMemberId,
      emailSent: result.data.emailSent,
      failedGroups: result.data.failedGroups || [],
    };
  }

  async activateEmployee(token: string, password: string): Promise<{ email: string }> {
    const fn = httpsCallable<any, { success: boolean; email: string }>(this.functions, 'activateEmployee');
    const result = await fn({ token, password });
    return { email: result.data.email };
  }

  async resendInvitation(teamMemberId: string): Promise<{ emailSent: boolean }> {
    const fn = httpsCallable<any, { success: boolean; emailSent: boolean }>(this.functions, 'resendInvitation');
    const result = await fn({ teamMemberId });
    return { emailSent: result.data.emailSent };
  }

  // ── Partenaires d'expédition CRUD ──

  async getAllShippingPartners(): Promise<ShippingPartner[]> {
    const ref = collection(this.firestore, 'shippingPartners');
    const snap = await getDocs(ref);
    return snap.docs.map(d => {
      const data = d.data();
      return {
        id: d.id,
        name: data['name'] || '',
        shippingMethod: data['shippingMethod'] || 'air',
        subType: data['subType'],
        contact: data['contact'] || {},
        status: data['status'] || 'active',
        flightSchedules: data['flightSchedules'] || [],
        createdAt: data['createdAt']?.toDate?.() || new Date(),
        updatedAt: data['updatedAt']?.toDate?.() || new Date(),
      } as ShippingPartner;
    }).sort((a, b) => a.name.localeCompare(b.name));
  }

  async getShippingPartnersBySubType(subType: ShippingMethodSubType): Promise<ShippingPartner[]> {
    const all = await this.getAllShippingPartners();
    return all.filter(p => p.subType === subType && p.status === 'active');
  }

  async getShippingPartner(id: string): Promise<ShippingPartner | null> {
    const docRef = doc(this.firestore, 'shippingPartners', id);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return null;
    const data = snap.data();
    return {
      id: snap.id,
      name: data['name'] || '',
      shippingMethod: data['shippingMethod'] || 'air',
      subType: data['subType'],
      contact: data['contact'] || {},
      status: data['status'] || 'active',
      flightSchedules: data['flightSchedules'] || [],
      createdAt: data['createdAt']?.toDate?.() || new Date(),
      updatedAt: data['updatedAt']?.toDate?.() || new Date(),
    } as ShippingPartner;
  }

  async createShippingPartner(data: Partial<ShippingPartner>): Promise<string> {
    const ref = collection(this.firestore, 'shippingPartners');
    const now = new Date();
    const docRef = await addDoc(ref, {
      name: data.name || '',
      shippingMethod: data.shippingMethod || 'air',
      subType: data.subType || null,
      contact: data.contact || {},
      status: data.status || 'active',
      createdAt: now,
      updatedAt: now,
    });
    return docRef.id;
  }

  async updateShippingPartner(id: string, data: Partial<ShippingPartner>): Promise<void> {
    const ref = doc(this.firestore, 'shippingPartners', id);
    const updateData: any = { ...data, updatedAt: new Date() };
    delete updateData.id;
    delete updateData.createdAt;
    await updateDoc(ref, updateData);
  }

  async deleteShippingPartner(id: string): Promise<void> {
    const ref = doc(this.firestore, 'shippingPartners', id);
    await deleteDoc(ref);
  }

  /**
   * Vérifie si un partenaire d'expédition est utilisé dans des containers en cours.
   * Un partenaire est "en cours" si lié à un container avec status closed/dispatched/in_transit.
   */
  async isShippingPartnerInUse(partnerId: string): Promise<boolean> {
    const containersRef = collection(this.firestore, 'containers');
    const snap = await getDocs(containersRef);
    const activeStatuses = ['closed', 'dispatched', 'in_transit'];
    return snap.docs.some(d => {
      const data = d.data();
      return data['shippingMethodDetails']?.['partnerId'] === partnerId
        && activeStatuses.includes(data['status']);
    });
  }

  /**
   * Archive un partenaire d'expédition en cours d'utilisation et crée une V2.
   * L'ancien partenaire passe en 'archived', le nouveau est 'active'.
   */
  async archiveAndCreateV2(partnerId: string): Promise<string> {
    const existing = await this.getShippingPartner(partnerId);
    if (!existing) throw new Error('Partenaire introuvable');

    const now = new Date();
    const dateStr = now.toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit', year: 'numeric' });

    // 1. Créer le nouveau partenaire (V2)
    const newName = existing.name.replace(/ \(V\d+\)$/, '') + ' (V2)';
    const newRef = collection(this.firestore, 'shippingPartners');
    const newDoc = await addDoc(newRef, {
      name: newName,
      shippingMethod: existing.shippingMethod,
      subType: existing.subType || null,
      contact: existing.contact || {},
      flightSchedules: existing.flightSchedules || [],
      status: 'active',
      createdAt: now,
      updatedAt: now,
    });

    // 2. Archiver l'ancien partenaire
    const oldRef = doc(this.firestore, 'shippingPartners', partnerId);
    await updateDoc(oldRef, {
      status: 'archived',
      replacedById: newDoc.id,
      archivedReason: `Remplacé par V2 le ${dateStr}`,
      updatedAt: now,
    });

    return newDoc.id;
  }

  // ── Team logistique CRUD ──

  async getAllLogisticsTeamMembers(): Promise<LogisticsTeamMember[]> {
    const ref = collection(this.firestore, 'logisticsTeam');
    const snap = await getDocs(ref);
    return snap.docs.map(d => {
      const data = d.data();
      return {
        id: d.id,
        name: data['name'] || '',
        pseudonyme: data['pseudonyme'] || '',
        phoneOperator: data['phoneOperator'] || '',
        phoneWhatsApp: data['phoneWhatsApp'] || '',
        role: data['role'] || 'responsable_groupage',
        photoUrl: data['photoUrl'] || '',
        isInternal: data['isInternal'] ?? true,
        status: data['status'] || 'active',
        createdAt: data['createdAt']?.toDate?.() || new Date(),
        updatedAt: data['updatedAt']?.toDate?.() || new Date(),
      } as LogisticsTeamMember;
    }).sort((a, b) => a.name.localeCompare(b.name));
  }

  async getLogisticsTeamByRole(role: LogisticsRole): Promise<LogisticsTeamMember[]> {
    const all = await this.getAllLogisticsTeamMembers();
    return all.filter(m => m.role === role && m.status === 'active');
  }

  async getLogisticsTeamMember(id: string): Promise<LogisticsTeamMember | null> {
    const docRef = doc(this.firestore, 'logisticsTeam', id);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return null;
    const data = snap.data();
    return {
      id: snap.id,
      name: data['name'] || '',
      pseudonyme: data['pseudonyme'] || '',
      phoneOperator: data['phoneOperator'] || '',
      phoneWhatsApp: data['phoneWhatsApp'] || '',
      role: data['role'] || 'responsable_groupage',
      photoUrl: data['photoUrl'] || '',
      isInternal: data['isInternal'] ?? true,
      status: data['status'] || 'active',
      createdAt: data['createdAt']?.toDate?.() || new Date(),
      updatedAt: data['updatedAt']?.toDate?.() || new Date(),
    } as LogisticsTeamMember;
  }

  async createLogisticsTeamMember(data: Partial<LogisticsTeamMember>): Promise<string> {
    const ref = collection(this.firestore, 'logisticsTeam');
    const now = new Date();
    const docRef = await addDoc(ref, {
      name: data.name || '',
      pseudonyme: data.pseudonyme || '',
      phoneOperator: data.phoneOperator || '',
      phoneWhatsApp: data.phoneWhatsApp || '',
      role: data.role || 'responsable_groupage',
      photoUrl: data.photoUrl || '',
      isInternal: data.isInternal ?? true,
      status: data.status || 'active',
      createdAt: now,
      updatedAt: now,
    });
    return docRef.id;
  }

  async updateLogisticsTeamMember(id: string, data: Partial<LogisticsTeamMember>): Promise<void> {
    const ref = doc(this.firestore, 'logisticsTeam', id);
    const updateData: any = { ...data, updatedAt: new Date() };
    delete updateData.id;
    delete updateData.createdAt;
    await updateDoc(ref, updateData);
  }

  async deleteLogisticsTeamMember(id: string): Promise<void> {
    const ref = doc(this.firestore, 'logisticsTeam', id);
    await deleteDoc(ref);
  }
}
